import React, { useState } from 'react';
import { Badge } from 'reactstrap';
import TableBuilder, { ApiRequest, ModalButtonBuilder, ToastBuilder, LabelWithSubtext } from '../../../../../../../../../GlobalComponents/Builders';
import { sortArrayByKey, showRelativeTime } from '../../../../../../../../../GlobalComponents/Helpers';
import { AlertTriangle, RefreshCw } from 'react-feather';

/**
 * DriftReportModal — Shows:
 * 1. Stale values (exists = 0) — deleted from source API
 * 2. Drift history (dmt_value_drift) — values that were renamed or reparented
 */

const DriftReportModal = ({ jobData }) => {

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const fetchDriftData = () => {
        setLoading(true);
        ApiRequest('GET', '/dmt/jobs/{jobId}/supplementaryData/values', {
            pathParams: { jobId: jobData.id },
            queryParams: { showStale: true },
        }).then(res => {
                setData(res?.data || { stale: [], driftHistory: [] });
                setLoading(false);
            })
            .catch(() => {
                ToastBuilder('error', 'Failed to fetch drift data');
                setLoading(false);
            });
    };

    const staleValues = data?.stale || [];
    const driftHistory = data?.driftHistory || [];

    const groupedStale = staleValues.reduce((acc, v) => {
        const key = v.resource_name || `Resource ${v.resource_id}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(v);
        return acc;
    }, {});

    return (
        <ModalButtonBuilder
            title='Drift Report'
            className='btn-warning'
            modalTitle='Value Drift Report'
            size='lg'
            onSubmit={false}
            onCancelLabel='Close'
            onClick={fetchDriftData}
            body={
                <div>
                    {loading && <p className='text-muted'>Loading...</p>}

                    {!loading && data && staleValues.length === 0 && driftHistory.length === 0 && (
                        <div className='text-center py-4'>
                            <p className='text-muted'>No drift detected. All values are in sync.</p>
                        </div>
                    )}

                    {/* Stale values section */}
                    {!loading && staleValues.length > 0 && (
                        <div className='mb-4'>
                            <h6 className='d-flex align-items-center gap-2 mb-2'>
                                <AlertTriangle size={14} color='#dc3545' />
                                Deleted Values
                                <Badge color='danger' pill style={{ fontSize: 10 }}>{staleValues.length}</Badge>
                            </h6>
                            <p style={{ fontSize: 11, color: '#999', marginBottom: 8 }}>Values no longer found in the source API after the last refresh.</p>
                            {Object.entries(groupedStale).map(([resourceName, values]) => (
                                <div key={resourceName} className='mb-3'>
                                    <div className='d-flex align-items-center gap-2 mb-1'>
                                        <strong style={{ fontSize: 12 }}>{resourceName}</strong>
                                        <Badge color='secondary' pill style={{ fontSize: 9 }}>{values.length}</Badge>
                                    </div>
                                    <TableBuilder
                                        data={sortArrayByKey(values, 'value')}
                                        processing={false}
                                        pagination={values.length > 15}
                                        pageSize={15}
                                        columns={[
                                            {
                                                name: 'Value ID',
                                                selector: (row) => row.value_id,
                                                sortable: true,
                                                center: true,
                                                maxWidth: '20%',
                                            },
                                            {
                                                name: 'Value',
                                                selector: (row) => <LabelWithSubtext text={row.value} subtext={row.parent_name} />,
                                                sortable: true,
                                            },
                                            {
                                                name: 'Source',
                                                selector: (row) => <Badge color={row.source === 'import' ? 'info' : 'secondary'} pill style={{ fontSize: 9 }}>{row.source || 'api'}</Badge>,
                                                center: true,
                                                maxWidth: '12%',
                                            },
                                        ]}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Drift history section */}
                    {!loading && driftHistory.length > 0 && (
                        <div>
                            <h6 className='d-flex align-items-center gap-2 mb-2'>
                                <RefreshCw size={14} color='#e67e22' />
                                Recent Changes
                                <Badge color='warning' pill style={{ fontSize: 10 }}>{driftHistory.length}</Badge>
                            </h6>
                            <p style={{ fontSize: 11, color: '#999', marginBottom: 8 }}>Values that were renamed or reparented during the last refresh.</p>
                            <TableBuilder
                                data={driftHistory}
                                processing={false}
                                pagination={driftHistory.length > 20}
                                pageSize={20}
                                subHeader={true}
                                columnsToSearch={['previous_value', 'current_value', 'resource_name']}
                                columns={[
                                    {
                                        name: 'Resource',
                                        selector: (row) => row.resource_name,
                                        sortable: true,
                                        maxWidth: '18%',
                                    },
                                    {
                                        name: 'Previous',
                                        selector: (row) => <LabelWithSubtext text={row.previous_value} subtext={row.previous_parent} />,
                                        sortable: true,
                                    },
                                    {
                                        name: 'Current',
                                        selector: (row) => row.exists === 0
                                            ? <span className='txt-danger'>Deleted</span>
                                            : <LabelWithSubtext text={row.current_value} subtext={row.current_parent} />,
                                        sortable: true,
                                    },
                                    {
                                        name: 'When',
                                        selector: (row) => row.detected_at ? showRelativeTime(row.detected_at * 1000) : '—',
                                        sortable: true,
                                        center: true,
                                        maxWidth: '15%',
                                    },
                                ]}
                            />
                        </div>
                    )}
                </div>
            }
        />
    );
};

export default DriftReportModal;
